import { useState, useEffect } from 'react';
import { useCompanyStore } from '@/lib/stores';
import { useCompanyReviews } from '@/hooks/useCompanyReviews';
import { useJobCategoryFilter } from '@/hooks/useJobCategoryFilter';
import { supabase } from '@/integrations/supabase/client';
import { Company } from '@/lib/types';
import { useToast } from '@/hooks/use-toast';

export const useCompanyProfileData = (paramId: string) => {
  const { companies, fetchCompanies } = useCompanyStore();
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 3;
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [companyData, setCompanyData] = useState<Company | null>(null);
  
  const { allCompanyReviews, decodedCompanyName } = useCompanyReviews(paramId || '');
  
  const { selectedCategory, setSelectedCategory, filteredReviews, JOB_CATEGORIES } = useJobCategoryFilter(allCompanyReviews);
  
  // First, try to find the company in the store
  const storeCompany = companies.find(
    c => c.name.toLowerCase() === decodedCompanyName.toLowerCase()
  );
  
  // Fetch companies from Supabase if they don't exist in the store
  useEffect(() => {
    const loadCompanies = async () => {
      setIsLoading(true);
      if (companies.length === 0) {
        await fetchCompanies();
      }
      
      // If we have a decodedCompanyName but no company in the store,
      // try to fetch it directly from Supabase
      if (decodedCompanyName && !storeCompany) {
        try {
          console.log("Searching for company:", decodedCompanyName);
          
          const { data, error } = await supabase
            .from('companies')
            .select('*')
            .ilike('name', decodedCompanyName)
            .single();
            
          if (error) {
            console.error("Error fetching company:", error);
            
            // Try a more lenient search if exact match fails
            const { data: fuzzyData, error: fuzzyError } = await supabase
              .from('companies')
              .select('*')
              .ilike('name', `%${decodedCompanyName}%`)
              .order('name', { ascending: true })
              .limit(1);
              
            if (fuzzyError || !fuzzyData || fuzzyData.length === 0) {
              console.error("Error in fuzzy search:", fuzzyError || "No results");
            } else if (fuzzyData && fuzzyData.length > 0) {
              console.log("Found company via fuzzy search:", fuzzyData[0]);
              // Convert Supabase data to our app's Company format
              const company: Company = {
                id: fuzzyData[0].id,
                name: fuzzyData[0].name,
                locations: fuzzyData[0].locations || [],
                createdAt: new Date(fuzzyData[0].created_at).getTime(),
                bio: fuzzyData[0].bio || '',
                staffSize: fuzzyData[0].staff_size || '',
                logo: fuzzyData[0].logo || '',
                bannerImage: fuzzyData[0].banner_image || '',
                website: fuzzyData[0].website || '',
                twitterUrl: fuzzyData[0].twitter_url || '',
                instagramUrl: fuzzyData[0].instagram_url || '',
                linkedinUrl: fuzzyData[0].linkedin_url || '',
                isVisible: fuzzyData[0].is_visible
              };
              setCompanyData(company);
            }
          } else if (data) {
            console.log("Found company:", data);
            // Convert Supabase data to our app's Company format
            const company: Company = {
              id: data.id,
              name: data.name,
              locations: data.locations || [],
              createdAt: new Date(data.created_at).getTime(),
              bio: data.bio || '',
              staffSize: data.staff_size || '',
              logo: data.logo || '',
              bannerImage: data.banner_image || '',
              website: data.website || '',
              twitterUrl: data.twitter_url || '',
              instagramUrl: data.instagram_url || '',
              linkedinUrl: data.linkedin_url || '',
              isVisible: data.is_visible
            };
            setCompanyData(company);
          }
        } catch (err) {
          console.error("Error in fetching company data:", err);
          toast({
            title: "Error",
            description: "Could not load company information",
            variant: "destructive"
          });
        }
      }
      setIsLoading(false);
    };
    
    loadCompanies();
  }, [companies.length, decodedCompanyName, fetchCompanies, storeCompany, toast]);
  
  useEffect(() => {
    setCurrentPage(1);
  }, [paramId, selectedCategory]);
  
  // Use either the company from the store or the one we fetched directly
  const company = storeCompany || companyData;
  
  // Debug company data
  useEffect(() => {
    if (company) {
      console.log("Company data loaded:", company);
      console.log("Logo URL:", company.logo);
      console.log("Banner Image URL:", company.bannerImage);
    }
  }, [company]);
  
  const positiveExperiences = filteredReviews.filter(review => review.positiveExperience === true).length;
  const negativeExperiences = filteredReviews.filter(review => review.positiveExperience === false).length;
  const fairWorkCount = filteredReviews.filter(review => review.fairWorkInvolved === true).length;
  
  const companyStats = {
    totalReviews: filteredReviews.length,
    locations: [...new Set(filteredReviews.map(review => review.location))],
  };
  
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredReviews.slice(indexOfFirstReview, indexOfLastReview);
  
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return {
    company,
    decodedCompanyName,
    isLoading,
    companyStats,
    reviewData: {
      filteredReviews,
      positiveExperiences,
      negativeExperiences,
      fairWorkCount,
      currentPage,
      totalPages,
      currentReviews,
      handlePageChange,
    },
    categoryData: {
      selectedCategory,
      setSelectedCategory,
      JOB_CATEGORIES
    }
  };
};
